import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestApi } from 'hooks/useRestApi';
import { useSnackbar } from 'hooks/useSnackbar';
import { apiUrl } from 'utils/apiUrl';

/**
 * A single record of the reaper's death row, as returned by
 * `GET /lct/api/v1/reaperLogs` (lct-site-reaper).
 */
export interface ReaperLogEntry {
  InstallationId?: string;
  TenantId?: string;
  FleetId?: string;
  SiteUrl?: string;
  CreatedBy?: string;
  Band?: string;
  LearnVersion?: string;
  SentenceUpdated?: number;
  ReaperCountdownEndTime?: number;
  MonthlyCost?: string;
  Reason?: string;
}

interface ReaperLogResponse {
  items: ReaperLogEntry[];
  next_token: Record<string, string> | null;
}

// The reaper scans DynamoDB with a filter, so a page can come back mostly empty
// and still carry a next_token. Ask for large pages to keep the walk short, and
// cap the walk so a pathological table can never hang the browser.
const PAGE_LENGTH = 1000;
const HISTORY_DAYS = 30;
const MAX_PAGES = 100;

const CSV_COLUMNS: Array<keyof ReaperLogEntry> = [
  'InstallationId',
  'TenantId',
  'FleetId',
  'SiteUrl',
  'CreatedBy',
  'Band',
  'LearnVersion',
  'SentenceUpdated',
  'ReaperCountdownEndTime',
  'MonthlyCost',
  'Reason',
];

// Epoch seconds as stored on the death row table.
const TIMESTAMP_COLUMNS = new Set<keyof ReaperLogEntry>([
  'SentenceUpdated',
  'ReaperCountdownEndTime',
]);

const toGmtString = (epochSeconds?: number): string => {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toISOString().replace('T', ' ').substring(0, 19);
};

const escapeCsvField = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value)
    .replace(/[\r\n]+/g, ' ')
    .trim();
  if (stringValue.includes(';') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const generateReaperLogCsv = (entries: ReaperLogEntry[]): string => {
  const headers = CSV_COLUMNS.map((column) =>
    TIMESTAMP_COLUMNS.has(column) ? `${column} (GMT YYYY-MM-DD HH:mm:ss)` : column,
  );

  let csvContent = `${headers.join(';')}\r\n`;

  entries.forEach((entry) => {
    const row = CSV_COLUMNS.map((column) =>
      escapeCsvField(
        TIMESTAMP_COLUMNS.has(column) ? toGmtString(entry[column] as number) : entry[column],
      ),
    );
    csvContent += `${row.join(';')}\r\n`;
  });

  return csvContent;
};

const triggerFileDownload = (content: string, fileName: string) => {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Downloads the site reaper's clean up log as a CSV file.
 *
 * The endpoint is paginated, so this walks every page before building the file.
 */
export const useReaperLogDownload = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);

  const reaperLogApi = useRestApi<ReaperLogResponse>(apiUrl('lct', 'reaperLogs'), {
    manual: true,
  });
  const { fetch } = reaperLogApi;

  const downloadCleanUpLog = useCallback(async () => {
    setIsDownloading(true);

    let entries: ReaperLogEntry[] = [];
    let truncated = false;

    try {
      let nextToken: Record<string, string> | null = null;
      let page = 0;

      do {
        const params: Record<string, string | number> = {
          history_days: HISTORY_DAYS,
          page_length: PAGE_LENGTH,
        };
        // The API models next_token as an object; send it as JSON so it survives
        // the query string instead of being flattened into bracketed keys.
        if (nextToken) params.next_token = JSON.stringify(nextToken);

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch({ params });

          entries = [...entries, ...(response?.data?.items ?? [])];
          nextToken = response?.data?.next_token ?? null;
        } catch (pageError) {
          if (page === 0) throw pageError;
          truncated = true;
          nextToken = null;
        }

        page += 1;
      } while (nextToken && page < MAX_PAGES);

      if (nextToken) truncated = true;

      if (entries.length === 0) {
        enqueueSnackbar(t('learnSites.cleanUpLogEmpty'), { variant: 'info' });
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      triggerFileDownload(generateReaperLogCsv(entries), `clean_up_log_${timestamp}.csv`);

      if (truncated) {
        enqueueSnackbar(t('learnSites.cleanUpLogTruncated', { count: entries.length }), {
          variant: 'warning',
        });
      }
    } catch (error: any) {
      enqueueSnackbar(t('learnSites.cleanUpLogError', { message: error?.message ?? '' }), {
        variant: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [enqueueSnackbar, fetch, t]);

  return { downloadCleanUpLog, isDownloading };
};

export default useReaperLogDownload;
